/**
 * CreatorCastAssets — NCC-5 one-time DOM setup for the web cast.
 *
 * The 36 Nova Creator Cast scenes at `/public/creator-cast/scenes/`
 * are SVG fragments authored on a 600×400 viewBox. They rely on two
 * ambient resources that the fragments themselves don't carry:
 *
 *   1. `creator-cast.css` — the shared motion vocabulary (`.bob`,
 *      `.tilt`, `.blink`, `.twinkle`, `.flash`, `.pass`, etc.).
 *      Without the stylesheet, every scene renders as a static
 *      illustration.
 *
 *   2. `defs.svg` — the shared `<defs>` block with the `#sb`
 *      starburst symbol. Every scene file also carries its own
 *      inline copy of the defs, so `<use href="#sb">` works either
 *      way — but injecting the shared defs at document scope is
 *      cheap insurance and matches what the standalone gallery
 *      does. Adds a `data-creator-cast-defs` attribute so we can
 *      guard against re-injection on hot-reload / Suspense retries.
 *
 * Mount once at `<App>` root. Every downstream `CreatorSceneEmbed`
 * relies on this having run at least once — the stylesheet has to be
 * in the head before the first scene paints or the animations won't
 * pick up the class-selector rules.
 *
 * The component renders nothing.
 */
import { useEffect } from 'react';

const STYLESHEET_HREF = '/creator-cast/creator-cast.css';
const STYLESHEET_MARKER = 'data-creator-cast';
const DEFS_URL = '/creator-cast/defs.svg';
const DEFS_MARKER = 'data-creator-cast-defs';

let defsFetchStarted = false;

function ensureStylesheet(): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[${STYLESHEET_MARKER}]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = STYLESHEET_HREF;
  link.setAttribute(STYLESHEET_MARKER, '1');
  document.head.appendChild(link);
}

function ensureDefs(): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`svg[${DEFS_MARKER}]`)) return;
  if (defsFetchStarted) return;
  defsFetchStarted = true;
  fetch(DEFS_URL)
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
    .then((text) => {
      // Bail if a previous mount raced ahead of us.
      if (document.querySelector(`svg[${DEFS_MARKER}]`)) return;
      const wrap = document.createElement('div');
      wrap.innerHTML = text;
      const svg = wrap.querySelector('svg');
      if (!svg) return;
      svg.setAttribute(DEFS_MARKER, '1');
      // Fully invisible + non-interactive; the defs just need to live in the DOM.
      svg.setAttribute('width', '0');
      svg.setAttribute('height', '0');
      svg.setAttribute('aria-hidden', 'true');
      svg.style.position = 'absolute';
      svg.style.width = '0';
      svg.style.height = '0';
      svg.style.overflow = 'hidden';
      svg.style.pointerEvents = 'none';
      document.body.appendChild(svg);
    })
    .catch(() => {
      // Non-fatal — every scene ships an inline defs copy too, so
      // `<use href="#sb">` will still resolve against the scene's own
      // defs. Reset the fetch flag so a later remount can retry.
      defsFetchStarted = false;
    });
}

/**
 * Prime the shared cast assets on the client. Idempotent — safe to
 * mount in multiple places (e.g. under `<App>` and inside a route-
 * level shell during hot reload).
 */
export function CreatorCastAssets() {
  useEffect(() => {
    ensureStylesheet();
    ensureDefs();
  }, []);
  return null;
}

export default CreatorCastAssets;

// Exposed so `CreatorSceneEmbed` can call the primers directly on its
// own mount as a safety net — a rendered scene without the stylesheet
// looks static and confusing, so we belt-and-suspenders it.
export { ensureStylesheet as ensureCreatorCastStylesheet, ensureDefs as ensureCreatorCastDefs };
